import React from 'react';
import { Editable } from '@ark-ui/react/editable';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export type EditableInputProps = BaseInputProps<string> & {
  /** Lokalizovaný popisek tlačítka úprav (default český). */
  editLabel?: string;
  /** Lokalizované popisky potvrzení/zrušení editace pro AT. */
  submitLabel?: string;
  cancelLabel?: string;
  className?: string;
};

/**
 * Atomický „inline" editovatelný text postavený na Ark UI `Editable`.
 *
 * V náhledovém režimu zobrazuje hodnotu; po kliknutí přepne na editaci.
 * `id` z `<FormField />` jde přes `ids.input` — přímé přepsání `id` na
 * `<Editable.Input>` by rozbilo zag `getElementById` lookupy (commit na blur,
 * fokus při přepnutí do editace). Touch (RHF `onBlur`) se hlásí při commitu
 * hodnoty (`onValueCommit`), tj. po dokončení editace.
 */
export const EditableInput: React.FC<EditableInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  editLabel = 'Upravit',
  submitLabel = 'Uložit',
  cancelLabel = 'Zrušit',
  className,
}) => (
  <Editable.Root
    name={name}
    ids={{ input: id, label: labelId }}
    value={value ?? ''}
    placeholder={placeholder}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    translations={{
      edit: editLabel,
      submit: submitLabel,
      cancel: cancelLabel,
      input: testAttributes['data-label'],
    }}
    onValueChange={(details) => onChange(details.value)}
    onValueCommit={() => onBlur()}
    className={clsx(className)}
  >
    <Editable.Area className={styles.editableArea}>
      <Editable.Input
        ref={ref}
        className={styles.editableInput}
        aria-describedby={describedBy}
        aria-labelledby={labelId}
        {...testAttributes}
      />
      <Editable.Preview className={styles.editablePreview} />
    </Editable.Area>
    <Editable.Control>
      <Editable.EditTrigger asChild>
        <button
          type="button"
          className={styles.passwordTrigger}
          aria-label={editLabel}
        >
          <Icon name="pencil" size={16} />
        </button>
      </Editable.EditTrigger>
    </Editable.Control>
  </Editable.Root>
);

EditableInput.displayName = 'EditableInput';
